/**
 * Message Queue System with Retry Logic
 * Guarantees message delivery with persistent storage, exponential backoff, and offline handling
 */

const MESSAGE_QUEUE_KEY = 'message_queue';
const MAX_RETRY_ATTEMPTS = 5;
const INITIAL_RETRY_DELAY = 1000; // 1 second
const MAX_RETRY_DELAY = 60000; // 60 seconds
const QUEUE_PROCESS_INTERVAL = 5000; // Process queue every 5 seconds

export const MESSAGE_STATUS = {
  PENDING: 'pending',
  SENDING: 'sending',
  SENT: 'sent',
  FAILED: 'failed',
  DELIVERED: 'delivered'
};

/**
 * Message Queue Manager
 */
class MessageQueueManager {
  constructor() {
    this.queue = this.loadQueue();
    this.isProcessing = false;
    this.processingInterval = null;
    this.onlineStatusListener = null;
    this.statusUpdateCallbacks = new Map();
  }

  /**
   * Load queue from localStorage
   * @returns {Array} Queue array
   */
  loadQueue() {
    try {
      const stored = localStorage.getItem(MESSAGE_QUEUE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('❌ Error loading message queue:', error);
      return [];
    }
  }

  /**
   * Save queue to localStorage
   */
  saveQueue() {
    try {
      localStorage.setItem(MESSAGE_QUEUE_KEY, JSON.stringify(this.queue));
    } catch (error) {
      console.error('❌ Error saving message queue:', error);
    }
  }

  /**
   * Generate unique message ID
   * @returns {string} Unique ID
   */
  generateMessageId() {
    return `msg_${Date.now()}_${Math.random()?.toString(36)?.substr(2, 9)}`;
  }

  /**
   * Calculate exponential backoff delay
   * @param {number} attemptNumber - Current attempt number
   * @returns {number} Delay in milliseconds
   */
  calculateBackoffDelay(attemptNumber) {
    const delay = INITIAL_RETRY_DELAY * Math.pow(2, attemptNumber - 1);
    return Math.min(delay, MAX_RETRY_DELAY);
  }

  /**
   * Add message to queue
   * @param {Object} messageData - Message data
   * @returns {string} Queue message ID
   */
  enqueue(messageData) {
    const queueMessage = {
      id: this.generateMessageId(),
      conversationId: messageData?.conversationId,
      receiverId: messageData?.receiverId,
      content: messageData?.content,
      messageType: messageData?.messageType || 'text',
      status: MESSAGE_STATUS?.PENDING,
      attemptCount: 0,
      createdAt: new Date()?.toISOString(),
      lastAttemptAt: null,
      nextRetryAt: new Date()?.toISOString(),
      error: null,
      tempId: messageData?.tempId // Temporary ID for UI optimistic updates
    };

    this.queue?.push(queueMessage);
    this.saveQueue();

    console.log(`📥 Message queued: ${queueMessage?.id}`);

    // Start processing if not already running
    if (!this.isProcessing) {
      this.startProcessing();
    }

    return queueMessage?.id;
  }

  /**
   * Remove message from queue
   * @param {string} messageId - Queue message ID
   */
  dequeue(messageId) {
    const index = this.queue?.findIndex(msg => msg?.id === messageId);
    if (index !== -1) {
      this.queue?.splice(index, 1);
      this.saveQueue();
      console.log(`📤 Message dequeued: ${messageId}`);
    }
  }

  /**
   * Update message status
   * @param {string} messageId - Queue message ID
   * @param {string} status - New status
   * @param {Object} additionalData - Additional data to update
   */
  updateMessageStatus(messageId, status, additionalData = {}) {
    const message = this.queue?.find(msg => msg?.id === messageId);
    if (message) {
      message.status = status;
      Object.assign(message, additionalData);
      this.saveQueue();

      // Notify status change callbacks
      const callback = this.statusUpdateCallbacks?.get(messageId);
      if (callback) {
        callback(status, message);
      }

      console.log(`🔄 Message status updated: ${messageId} -> ${status}`);
    }
  }

  /**
   * Register status update callback for specific message
   * @param {string} messageId - Queue message ID
   * @param {Function} callback - Callback function
   */
  onStatusUpdate(messageId, callback) {
    this.statusUpdateCallbacks?.set(messageId, callback);
  }

  /**
   * Unregister status update callback
   * @param {string} messageId - Queue message ID
   */
  offStatusUpdate(messageId) {
    this.statusUpdateCallbacks?.delete(messageId);
  }

  /**
   * Process queue - send pending messages
   */
  async processQueue() {
    if (this.isProcessing) {
      return;
    }

    this.isProcessing = true;

    try {
      // Check if online
      if (!navigator.onLine) {
        console.log('📡 Offline - skipping queue processing');
        return;
      }

      const now = new Date();
      const pendingMessages = this.queue?.filter(
        msg => 
          (msg?.status === MESSAGE_STATUS?.PENDING || msg?.status === MESSAGE_STATUS?.FAILED) &&
          msg?.attemptCount < MAX_RETRY_ATTEMPTS &&
          new Date(msg.nextRetryAt) <= now
      );

      if (pendingMessages?.length === 0) {
        return;
      }

      console.log(`🔄 Processing ${pendingMessages?.length} queued messages`);

      // Process messages sequentially to avoid overwhelming the server
      for (const queueMessage of pendingMessages) {
        await this.sendQueuedMessage(queueMessage);
      }

    } catch (error) {
      console.error('❌ Error processing queue:', error);
    } finally {
      this.isProcessing = false;
    }
  }

  /**
   * Send a queued message
   * @param {Object} queueMessage - Queued message object
   */
  async sendQueuedMessage(queueMessage) {
    try {
      // Update status to sending
      this.updateMessageStatus(queueMessage?.id, MESSAGE_STATUS?.SENDING, {
        lastAttemptAt: new Date()?.toISOString(),
        attemptCount: queueMessage?.attemptCount + 1
      });

      // Dynamic import to avoid circular dependency
      const { messagingService } = await import('../services/messagingService.js');

      // CRITICAL FIX: Send message with skipQueue=true to avoid infinite re-queuing
      let sentMessage;
      if (queueMessage?.conversationId) {
        sentMessage = await messagingService?.sendMessage(
          queueMessage?.conversationId,
          queueMessage?.content,
          { skipQueue: true } // CRITICAL: Skip re-queuing when processing queue
        );
      } else if (queueMessage?.receiverId) {
        sentMessage = await messagingService?.sendDirectMessage(
          queueMessage?.receiverId,
          queueMessage?.content,
          { skipQueue: true } // CRITICAL: Skip re-queuing when processing queue
        );
      } else {
        throw new Error('Missing conversation ID or receiver ID');
      }

      // Message sent successfully
      this.updateMessageStatus(queueMessage?.id, MESSAGE_STATUS?.SENT, {
        sentMessageId: sentMessage?.id,
        sentAt: new Date()?.toISOString(),
        error: null
      });

      // Remove from queue after successful send
      setTimeout(() => {
        this.dequeue(queueMessage?.id);
      }, 2000);

    } catch (error) {
      console.error(`❌ Failed to send queued message ${queueMessage?.id}:`, error);

      const updatedMessage = this.queue?.find(msg => msg?.id === queueMessage?.id);
      if (!updatedMessage) return;

      // Check if max retries reached
      if (updatedMessage?.attemptCount >= MAX_RETRY_ATTEMPTS) {
        this.updateMessageStatus(queueMessage?.id, MESSAGE_STATUS?.FAILED, {
          error: error?.message,
          failedAt: new Date()?.toISOString()
        });
        console.error(`❌ Message ${queueMessage?.id} failed after ${MAX_RETRY_ATTEMPTS} attempts`);
      } else {
        // Schedule retry with exponential backoff
        const backoffDelay = this.calculateBackoffDelay(updatedMessage?.attemptCount);
        const nextRetryAt = new Date(Date.now() + backoffDelay)?.toISOString();

        this.updateMessageStatus(queueMessage?.id, MESSAGE_STATUS?.PENDING, {
          nextRetryAt,
          error: error?.message
        });

        console.log(`🔄 Message ${queueMessage?.id} will retry in ${backoffDelay}ms (attempt ${updatedMessage?.attemptCount}/${MAX_RETRY_ATTEMPTS})`);
      }
    }
  }

  /**
   * Start queue processing
   */
  startProcessing() {
    if (this.processingInterval) {
      return;
    }

    console.log('🚀 Starting message queue processor');

    // Process immediately
    this.processQueue();

    // Set up interval processing
    this.processingInterval = setInterval(() => {
      this.processQueue();
    }, QUEUE_PROCESS_INTERVAL);

    // Listen for online status changes
    this.onlineStatusListener = () => {
      console.log('📡 Back online - processing queue');
      this.processQueue();
    };
    window.addEventListener('online', this.onlineStatusListener);
  }

  /**
   * Stop queue processing
   */
  stopProcessing() {
    if (this.processingInterval) {
      clearInterval(this.processingInterval);
      this.processingInterval = null;
      console.log('⏸️ Stopped message queue processor');
    }

    if (this.onlineStatusListener) {
      window.removeEventListener('online', this.onlineStatusListener);
      this.onlineStatusListener = null;
    }
  }

  /**
   * Retry failed message manually
   * @param {string} messageId - Queue message ID
   */
  retryMessage(messageId) {
    const message = this.queue?.find(msg => msg?.id === messageId);
    if (message && message?.status === MESSAGE_STATUS?.FAILED) {
      this.updateMessageStatus(messageId, MESSAGE_STATUS?.PENDING, {
        nextRetryAt: new Date()?.toISOString(),
        attemptCount: 0,
        error: null
      });
      this.processQueue();
    }
  }

  /**
   * Get queue status
   * @returns {Object} Queue statistics
   */
  getQueueStatus() {
    return {
      total: this.queue?.length,
      pending: this.queue?.filter(msg => msg?.status === MESSAGE_STATUS?.PENDING)?.length,
      sending: this.queue?.filter(msg => msg?.status === MESSAGE_STATUS?.SENDING)?.length,
      sent: this.queue?.filter(msg => msg?.status === MESSAGE_STATUS?.SENT)?.length,
      failed: this.queue?.filter(msg => msg?.status === MESSAGE_STATUS?.FAILED)?.length,
      isProcessing: this.isProcessing
    };
  }

  /**
   * Get failed messages
   * @returns {Array} Failed messages
   */
  getFailedMessages() {
    return this.queue?.filter(msg => msg?.status === MESSAGE_STATUS?.FAILED);
  }

  /**
   * Clear all messages from queue (use with caution)
   */
  clearQueue() {
    this.queue = [];
    this.saveQueue();
    this.statusUpdateCallbacks?.clear();
    console.log('🗑️ Message queue cleared');
  }

  /**
   * Clear sent messages from queue
   */
  clearSentMessages() {
    const originalLength = this.queue?.length;
    this.queue = this.queue?.filter(msg => msg?.status !== MESSAGE_STATUS?.SENT);
    this.saveQueue();
    console.log(`🗑️ Cleared ${originalLength - this.queue?.length} sent messages`);
  }
}

// Export singleton instance
export const messageQueue = new MessageQueueManager();

// Auto-start processing on module load
if (typeof window !== 'undefined') {
  messageQueue?.startProcessing();
}